<template>
  <main>
    <div v-if="loading" id="loading" class="text-center">
      <loading></loading>
    </div>
    <section v-if="!loading">
      <section v-if="showStartUpPlan">
        <b-row style="background: #F1F1F1;">
          <b-col cols="12" class="text-center pt-3">
            <strong>Site Start-Up Milestones </strong>
          </b-col>
          <b-col>
            <stepper :steps="steps"></stepper>
          </b-col>
        </b-row>
        <div class="px-4">
          <b-row class="">
            <b-col class="mt-3" cols="12" lg="6" md="6" sm="12" :xl="calcCols">
              <site-activation-in-progress
                :lastMilestoneAchieved="lastMilestoneAchieved"
                :milestones="milestones"
                :nextMilestonePlanned="nextMilestonePlanned"
                class="milestone-card-height"
              ></site-activation-in-progress>
            </b-col>
            <b-col class="mt-3 order-xl-last" cols="12" lg="6" md="6" sm="12" :xl="calcCols">
              <your-site-activation-checklist
                class="milestone-card-height"
              ></your-site-activation-checklist>
            </b-col>
            <b-col
              class="mt-3"
              cols="12"
              lg="12"
              md="12"
              sm="12"
              xl="4"
              v-if="canRedirectToApplicationAccess"
            >
              <application-access
                :canRedirectToApplicationAccess="canRedirectToApplicationAccess"
                :siteId="siteId"
                :studyId="studyId"
                class="milestone-card-height"
              ></application-access>
            </b-col>
          </b-row>
          <b-row class="pt-3">
            <b-col>
              <start-up-status :canRedirectToEnrollment="canRedirectToEnrollment"></start-up-status>
            </b-col>
          </b-row>
          <b-row class="pt-3">
            <b-col>
              <study-recruitment-period
                :canRedirectToEnrollment="canRedirectToEnrollment"
                :siteId="siteId"
                :studyId="studyId"
              ></study-recruitment-period>
            </b-col>
          </b-row>
        </div>
        <b-row v-show="lastRefresh" class="text-center mt-4 mb-2">
          <b-col cols="12">
            <div class="lastRefreshText">
              Last data update: <span style="color: blue">{{ lastRefresh }}</span>
            </div>
            <div style="font-size:12px">
              Updates to data contained on this page may be delayed - please contact your Pfizer
              point of contact with questions
            </div>
          </b-col>
        </b-row>
      </section>
      <div v-if="!showStartUpPlan" id="errors" class="alert alert-info text-center m-5">
        <strong> There are no milestones data for this study - site</strong>
      </div>
    </section>
  </main>
</template>

<script>
  import moment from 'moment';
  import Stepper from './Stepper';
  import SiteActivationInProgress from './SiteActivationInProgress';
  import ApplicationAccess from './ApplicationAccess';
  import YourSiteActivationChecklist from './YourSiteActivationChecklist';
  import StartUpStatus from './StartUpStatus';
  import StudyRecruitmentPeriod from './StudyRecruitmentPeriod';

  export default {
    components: {
      SiteActivationInProgress,
      ApplicationAccess,
      YourSiteActivationChecklist,
      StartUpStatus,
      StudyRecruitmentPeriod,
      Stepper,
    },
    metaInfo() {
      let metaTitle = this.$appName + this.studyId + '-' + this.siteId + ' Start-Up-Plan';
      return {
        title: metaTitle,
      };
    },
    props: {
      blockedModules: Array,
      user: Object,
      studySite: Object,
    },
    data() {
      return {
        siteStatus: '',
        lastRefresh: '',
        nextMilestonePlanned: 'N/A',
        lastMilestoneAchieved: 'N/A',
        milestones: [],
        studyId: '',
        siteId: '',
        loading: false,
        showStartUpPlan: false,
        tempArray: [],
        steps: [],
        standardSteps: [
          {
            title: 'Site Selection',
            order: 1,

            status: 'Untouched',
            plannedDate: '',
            actualDate: '',
            subSteps: [],
          },
          {
            title: 'IIP Sent to Site',
            order: 2,

            status: 'Untouched',
            plannedDate: '',
            actualDate: '',
            subSteps: [],
          },
          {
            title: 'IRB/IEC Submission',
            order: 3,

            status: 'Untouched',
            plannedDate: '',
            actualDate: '',
            subSteps: [],
          },
          {
            title: 'IRB/IEC Approval',
            order: 4,

            status: 'Untouched',
            plannedDate: '',
            actualDate: '',
            subSteps: [],
          },
          {
            title: 'Clinical Study Agreement Fully Executed',
            order: 5,

            status: 'Untouched',
            plannedDate: '',
            actualDate: '',
            subSteps: [
              {
                title: 'Budget Sent to Site',
                order: 1,
                parentOrder: 5,
                status: 'Untouched',
                plannedDate: '',
                actualDate: '',
              },
              {
                title: 'Contract Sent to Site',
                order: 2,
                parentOrder: 5,
                status: 'Untouched',
                plannedDate: '',
                actualDate: '',
              },
              {
                title: 'Budget Negotiated',
                order: 3,
                parentOrder: 5,
                status: 'Untouched',
                plannedDate: '',
                actualDate: '',
              },
              {
                title: 'Contract Negotiated',
                order: 4,
                parentOrder: 5,
                status: 'Untouched',
                plannedDate: '',
                actualDate: '',
              },
            ],
          },
          {
            title: 'Site Initiation',
            order: 6,

            status: 'Untouched',
            plannedDate: '',
            actualDate: '',
            subSteps: [],
          },
          {
            title: 'Site Activation',
            order: 7,

            status: 'Untouched',
            plannedDate: '',
            actualDate: '',
            subSteps: [],
          },
          {
            title: 'FSFV',
            order: 8,

            status: 'Untouched',
            plannedDate: '',
            actualDate: '',
            subSteps: [],
          },
        ],
        milestonesTitleMap: {
          'Site Selection Date': 'Site Selection',
          'IIP Sent to Site': 'IIP Sent to Site',
          'IRB Submission': 'IRB/IEC Submission',
          'IRB/IEC Approval Site': 'IRB/IEC Approval',
          'Site IRB Approval Date': 'IRB/IEC Approval',
          'CSA Fully Executed Date': 'Clinical Study Agreement Fully Executed',
          SIV: 'Site Initiation',
          'siv planned(scheduled / Siebel) date': 'Site Initiation',
          'Site Activated': 'Site Activation',
          'Site FSFV': 'FSFV',
          'Site FSFV Date': 'FSFV',
        },
        subMilestonesTitleMap: {
          'IIP Submission': 'IIP Submission',
          'IIP Approval': 'IIP Approval',
          'Budget Sent to Site': 'Budget Sent to Site',
          'Contract Sent to Site': 'Contract Sent to Site',
          'Budget Negotiated': 'Budget Negotiated',
          'Contract Negotiated': 'Contract Negotiated',
        },
      };
    },
    computed: {
      canRedirectToEnrollment() {
        let permissions = ['access my enrollments', 'access enrollment oversight'];
        let modules = ['Enrollment'];

        let isBlockedByStudyModules = this.blockedModules.some(x => modules.includes(x));
        let isBlockedByUserPermissions = !this.user['permissions'].some(x =>
          permissions.includes(x['name']),
        );

        return !isBlockedByStudyModules && !isBlockedByUserPermissions;
      },
      canRedirectToApplicationAccess() {
        return (
          !this.blockedModules.find(x => x === 'Application Access') &&
          this.user['permissions'].find(x => x['name'] === 'access application access') !==
            undefined
        );
      },
      calcCols() {
        return this.canRedirectToApplicationAccess ? '4' : '6';
      },
    },
    watch: {
      $route() {
        this.initialize();
      },
    },
    created() {
      this.initialize();
    },
    methods: {
      initialize() {
        this.showStartUpPlan = false;
        this.tempArray.splice(0, this.tempArray.length);
        this.steps.splice(0, this.steps.length);
        this.steps = _.cloneDeep(this.standardSteps);
        this.studyId = this.$route.params['studyId'];
        this.siteId = this.$route.params['siteId'];
        this.getSiteStudyMilestones();
      },
      async getSiteStudyMilestones() {
        try {
          this.loading = true;
          let params = {
            study: this.studyId,
            site: this.siteId,
          };
          let response = await axios.get(`/api/get-site-milestones`, { params: params });
          if (response['data']['items'].length > 0) {
            this.milestones = response['data']['items'];
            this.siteStatus = this.studySite['siteInfo']['simplifySiteStatus'];
            this.calcLastRefresh();
            this.calcSteps();
            this.calcStatuses();
            this.showStartUpPlan = true;
          }
        } catch (error) {
          console.error(error);
        }
        this.loading = false;
      },
      calcLastRefresh() {
        let lastRefreshValues = this.milestones
          .map(item => item['etlLoadTime'])
          .filter(item => item);
        if (lastRefreshValues.length > 0) {
          this.lastRefresh = moment(_.max(lastRefreshValues));
        }
      },
      calcSteps() {
        this.steps.forEach(step => {
          let milestone = this.milestones.find(
            el => this.milestonesTitleMap[el['milestoneName']] === step['title'],
          );
          if (milestone) {
            step['plannedDate'] = milestone['plannedDate'];
            step['actualDate'] = milestone['actualDate'];
          }
          if (step['subSteps'].length > 0) {
            step['subSteps'].forEach(subStep => {
              milestone = this.milestones.find(
                el => el['milestoneName'] === this.subMilestonesTitleMap[subStep['title']],
              );
              if (milestone) {
                subStep['plannedDate'] = milestone['plannedDate'];
                subStep['actualDate'] = milestone['actualDate'];
              }
            });
          }
        });
      },
      calcStatuses() {
        this.steps.forEach(el => {
          if (el['subSteps'].length > 0) {
            el['subSteps'].forEach(x => {
              this.tempArray.push(_.cloneDeep(x));
            });
          }
          this.tempArray.push(_.cloneDeep(el));
        });
        this.statuses();
        this.steps.splice(0, this.steps.length);
        this.returnToInitStepsArray(this.tempArray);
      },
      statuses() {
        let lastStatus = 'Untouched';
        let keepLastStepIndex;
        this.tempArray
          .slice()
          .reverse()
          .forEach((step, index) => {
            if (lastStatus === 'Untouched') {
              if (
                (this.siteStatus === 'Activated' && step['title'] === 'Site Activation') ||
                (this.siteStatus === 'Ongoing' && step['title'] === 'FSFV') ||
                (this.siteStatus === 'Selected' && step['title'] === 'Site Selection')
              ) {
                step['status'] = 'Completed';
              }
              step['status'] = step['actualDate'] ? 'Completed' : 'Untouched';
              if (step['status'] === 'Completed') {
                let found = Object.keys(this.subMilestonesTitleMap).indexOf(step['title']);
                this.lastMilestoneAchieved = found > -1 ? 'IRB/IEC Approval' : step['title'];
                keepLastStepIndex = this.tempArray.length - index;
              }
            }
            if (lastStatus === 'Completed') {
              step['status'] = 'Completed';
            }
            lastStatus = step['status'];
          });

        if (keepLastStepIndex < this.tempArray.length) {
          this.tempArray[keepLastStepIndex]['status'] = 'InProgress';
          let found = Object.keys(this.subMilestonesTitleMap).indexOf(
            this.tempArray[keepLastStepIndex]['title'],
          );
          this.nextMilestonePlanned =
            found > -1
              ? 'Clinical Study Agreement Fully Executed'
              : this.tempArray[keepLastStepIndex]['title'];
        }
      },
      returnToInitStepsArray() {
        this.tempArray.forEach(element => {
          if (!element['parentOrder']) {
            this.steps.push(element);
          }
        });
        this.tempArray.forEach(element => {
          if (element['parentOrder']) {
            let foundStepIndex = this.steps.findIndex(
              step => step['order'] === element['parentOrder'],
            );
            let foundSubStepIndex = this.steps[foundStepIndex]['subSteps'].findIndex(
              subStep => subStep['order'] === element['order'],
            );
            this.steps[foundStepIndex]['subSteps'][foundSubStepIndex] = element;
          }
        });
      },
    },
  };
</script>

<style scoped>
  .milestone-card-height {
    height: 100%;
  }
</style>
